'use client'

import type { ReactNode } from 'react'
import { useId, useRef, useSyncExternalStore, useMemo } from 'react'
import { getTokensConfig, getTokens, getResolvedValue } from '@systhemaui/core'

import clsx from 'clsx'
import Link from 'next/link'
import { usePathname } from 'next/navigation'

import { Swiper, SwiperSlide } from 'swiper/react'
import { FreeMode, Navigation, Pagination, Mousewheel } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper/types'

import type { SectionProps } from '@systhemaui/next'
import { Section, Paragraph, Image, Chip, ChipTitle, ChipIcon } from '@systhemaui/next'
import { Stack, Heading } from '@systhemaui/react'

import { getSysthemaConfigSync } from '@systhemaui/core'

const { breakpoints } = getTokensConfig() as { breakpoints: Record<string, string> }
const { responsiveSizing } = getTokens()

// Subscribe function that never notifies — used with useSyncExternalStore for stable client values
const emptySubscribe = () => () => {}

// Get transition classes - these may differ between server/client, so we apply them after hydration
function getTransitionClasses() {
  const config = getSysthemaConfigSync()
  return typeof config.packages?.react === 'object' && config.packages?.react?.enabled
    ? config.packages?.react?.transitionClasses
    : undefined
}
const clientTransitionClasses = typeof window !== 'undefined' ? getTransitionClasses() : undefined

// Capture timestamp once at module load — stable reference for useSyncExternalStore
const clientNow = typeof window !== 'undefined' ? Date.now() : 0

export interface EpisodeRowItem {
  title: string
  label?: string
  isNew?: boolean
  isLive?: boolean
  liveUntil?: string
  /** Raw "coming soon" flag from CMS — actual state is computed client-side based on makeAvailableAfter */
  isComingSoon?: boolean
  comingSoonText?: string
  makeAvailableAfter?: string
  thumbnailUrl: string
  /** Thumbnail to show while episode is in "coming soon" state */
  comingSoonThumbnailUrl?: string
  sourceType?: 'video' | 'stream'
  mediaUuid: string
  embedUuid?: string
  descriptionJSX?: ReactNode
}

type EpisodeRowProps = Omit<SectionProps, 'children'> & {
  title?: string
  chipLabel?: string
  chipHref?: string
  chipNewTab?: boolean
  items: EpisodeRowItem[]
}

const EpisodeRowClient = (props: EpisodeRowProps) => {
  const {
    theme = 'default',
    layoutBackground = 'base',
    items: originalItems,
    title,
    chipLabel,
    chipHref,
    chipNewTab,
    className,
    ...rest
  } = props

  // Stable client timestamp — no flash because useSyncExternalStore applies it synchronously
  // during hydration (unlike useState+useEffect which causes two paints).
  // clientNow is captured once at module load so the snapshot never changes between renders.
  const now = useSyncExternalStore(
    emptySubscribe,
    () => clientNow,
    () => 0,
  )

  const items = originalItems
  const pathname = usePathname()
  const encodedBackHref = useMemo(() => encodeURIComponent(pathname || '/'), [pathname])
  const id = useId()
  const episodeRowId = `${id.replace(/[^a-zA-Z0-9]/g, '')}`

  const navigationPrevRef = useRef<HTMLButtonElement>(null)
  const navigationNextRef = useRef<HTMLButtonElement>(null)
  const transitionClasses = useSyncExternalStore(
    emptySubscribe,
    () => clientTransitionClasses,
    () => undefined,
  )

  const handleSwiper = (swiper: SwiperType) => {
    // Use rAF to ensure navigation button refs are populated
    requestAnimationFrame(() => {
      if (!swiper.params || swiper.destroyed) return
      if (navigationPrevRef.current && navigationNextRef.current) {
        if (swiper.params.navigation && typeof swiper.params.navigation !== 'boolean') {
          swiper.params.navigation.prevEl = navigationPrevRef.current
          swiper.params.navigation.nextEl = navigationNextRef.current
          swiper.navigation.init()
          swiper.navigation.update()
        }
      }
    })
  }

  return (
    <Section
      theme={theme}
      layoutBackground={layoutBackground}
      className={clsx('episode-row group/episode-row relative', className)}
      {...rest}
    >
      {(title || chipHref) && (
        <Stack
          direction="row"
          justifyContent="between"
          alignItems="center"
          disableAnimation
          className="mb-4"
        >
          {title && (
            <Paragraph type="label" disableAnimation className="grow">
              {title}
            </Paragraph>
          )}
          {chipHref && (
            <Chip.a
              href={chipHref}
              target={chipNewTab ? '_blank' : undefined}
              rel={chipNewTab ? 'noopener noreferrer' : undefined}
            >
              {chipLabel && <ChipTitle>{chipLabel}</ChipTitle>}
              <ChipIcon.span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 -960 960 960"
                  fill="currentColor"
                >
                  <path d="M647-440H160v-80h487L423-744l57-56 320 320-320 320-57-56 224-224Z" />
                </svg>
              </ChipIcon.span>
            </Chip.a>
          )}
        </Stack>
      )}
      <Swiper
        id={episodeRowId}
        className="peer/swiper"
        modules={[FreeMode, Navigation, Pagination, Mousewheel]}
        slidesPerView={1.5}
        spaceBetween={parseInt(
          (getResolvedValue(responsiveSizing, 'gallery.slidesGapX', 'sm') as string) ?? '24',
        )}
        loop={false}
        navigation={true}
        pagination={false}
        freeMode={{
          enabled: true,
          momentum: true,
          momentumRatio: 0.5,
          momentumVelocityRatio: 0.5,
          sticky: true,
        }}
        touchEventsTarget="container"
        mousewheel={{
          forceToAxis: true,
          thresholdDelta: 100,
          releaseOnEdges: true,
        }}
        breakpoints={{
          [parseInt(breakpoints?.md as string) ?? 640]: {
            slidesPerView: 2,
            spaceBetween: parseInt(
              (getResolvedValue(responsiveSizing, 'gallery.slidesGapX', 'md') as string) ?? '24',
            ),
          },
          768: {
            slidesPerView: 3,
            spaceBetween: parseInt(
              (getResolvedValue(responsiveSizing, 'gallery.slidesGapX', 'md') as string) ?? '24',
            ),
          },
          [parseInt(breakpoints?.lg as string) ?? 1192]: {
            slidesPerView: 4,
            spaceBetween: parseInt(
              (getResolvedValue(responsiveSizing, 'gallery.slidesGapX', 'lg') as string) ?? '24',
            ),
          },
        }}
        onSwiper={handleSwiper}
      >
        {items?.map((item) => {
          // Compute isComingSoon client-side using the current timestamp.
          // When now=0 (server SSR): falls back to raw isComingSoon flag (conservative).
          // When now=Date.now() (client): performs the real date check.
          const isComingSoon =
            (item.isComingSoon ?? false) &&
            !(item.makeAvailableAfter && new Date(item.makeAvailableAfter).getTime() <= now)

          const isLive =
            !isComingSoon &&
            (item.isLive ?? false) &&
            !(item.liveUntil && new Date(item.liveUntil).getTime() <= now)

          const thumbnailUrl =
            isComingSoon && item.comingSoonThumbnailUrl
              ? item.comingSoonThumbnailUrl
              : item.thumbnailUrl

          const contentUuid = item.sourceType === 'stream' ? item.embedUuid : item.mediaUuid
          const contentRoute = item.sourceType === 'stream' ? 'stream' : 'video'
          const isLinked = !!(contentUuid && !isComingSoon)
          const cardContent = (
            <>
              {/* Thumbnail */}
              <div
                className={`${isComingSoon ? 'bg-black' : 'image-skeleton'} relative aspect-16/9 overflow-hidden`}
              >
                <Image
                  className={`absolute inset-0 aspect-auto h-full w-full ${isLinked ? 'transition-opacity duration-300 group-hover/episode-card:opacity-50' : ''} ${isComingSoon ? 'opacity-30' : ''}`}
                  src={thumbnailUrl}
                  alt={item.title}
                  objectFit="cover"
                  loading="lazy"
                  decoding="async"
                  fetchPriority="low"
                  sizes="
                    (max-width: 640px) 50vw,
                    (max-width: 1192px) 25vw,
                    20vw
                  "
                  disableAnimation
                />
                {isComingSoon && (
                  <div className="absolute inset-0 flex items-center justify-center bg-(--color-media-overlay)">
                    <span className="text-sm font-semibold tracking-wide text-white">
                      {item.comingSoonText || 'Hamarosan...'}
                    </span>
                  </div>
                )}
              </div>
              {/* Episode info */}
              <div className="flex flex-col gap-2.5 px-3 py-3 md:px-4">
                {(item.label ||
                  isComingSoon ||
                  isLive ||
                  (item.isNew && !isComingSoon && !isLive)) &&
                  (() => {
                    // Priority: Hamarosan > ÉLŐ > Új rész (only one badge at a time)
                    const badge = isComingSoon
                      ? item.comingSoonText
                        ? 'comingSoon'
                        : null
                      : isLive
                        ? 'live'
                        : item.isNew
                          ? 'new'
                          : null
                    return (
                      <div
                        className={`flex gap-2 md:items-center ${badge ? 'max-md:flex-col max-md:items-start' : ''}`}
                      >
                        {badge && (
                          <div className="flex items-center gap-x-2">
                            {badge === 'comingSoon' && (
                              <Chip.span className="text-small shrink-0 rounded-none bg-gray-500 px-2 py-1 leading-none font-semibold uppercase">
                                Hamarosan
                              </Chip.span>
                            )}
                            {badge === 'live' && (
                              <Chip.span className="text-small shrink-0 rounded-none bg-green-600 px-2 py-1 leading-none font-semibold uppercase">
                                <span className="animate-live-dot mr-0.5 inline-block size-2 rounded-full bg-white" />
                                ÉLŐ
                              </Chip.span>
                            )}
                            {badge === 'new' && (
                              <Chip.span className="text-small shrink-0 rounded-none bg-red-500 px-2 py-1 leading-none font-semibold uppercase">
                                Új rész
                              </Chip.span>
                            )}
                          </div>
                        )}
                        {item.label && (
                          <p className="text-small color-body line-clamp-3">{item.label}</p>
                        )}
                      </div>
                    )
                  })()}
                <Heading.h6>{item.title}</Heading.h6>
                {item.descriptionJSX && (
                  <div className="line-clamp-3 opacity-80 md:line-clamp-3">
                    {item.descriptionJSX}
                  </div>
                )}
              </div>
            </>
          )

          return (
            <SwiperSlide key={contentUuid || item.title}>
              {contentUuid && !isComingSoon ? (
                <Link
                  href={`/${contentRoute}/${contentUuid}?backHref=${encodedBackHref}`}
                  className="card-default group/episode-card h-full px-0 py-0 hover:border-white"
                >
                  {cardContent}
                </Link>
              ) : (
                <div className="card-default group/episode-card h-full px-0 py-0">
                  {cardContent}
                </div>
              )}
            </SwiperSlide>
          )
        })}
      </Swiper>
      <div className="ease-out-cubic absolute bottom-(--section-padding-y) left-0 z-10 grid h-[calc(100%-var(--section-padding-y)-40px)] w-48 place-items-center bg-linear-to-r from-blue-950 to-transparent opacity-0 transition-opacity duration-300 group-hover/episode-row:opacity-100 hover:opacity-100 has-disabled:pointer-events-none has-disabled:opacity-0! max-md:hidden">
        <button
          ref={navigationPrevRef}
          className={clsx(
            'episode-row-navigation-button button-primary cursor-pointer disabled:cursor-not-allowed disabled:bg-(--color-button-primary-normal-background)',
            transitionClasses,
          )}
          aria-controls={`${episodeRowId}-swiper`}
          aria-label="Previous Slide"
          tabIndex={0}
        >
          <svg className="button-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
            <path
              fill="currentColor"
              d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.2 288H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H109.3l105.3-105.4c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"
            />
          </svg>
          <span className="sr-only!">Previous</span>
        </button>
      </div>
      <div className="ease-out-cubic absolute right-0 bottom-(--section-padding-y) z-10 grid h-[calc(100%-var(--section-padding-y)-40px)] w-48 place-items-center bg-linear-to-l from-blue-950 to-transparent opacity-0 transition-opacity duration-300 group-hover/episode-row:opacity-100 hover:opacity-100 has-disabled:pointer-events-none has-disabled:opacity-0! max-md:hidden">
        <button
          ref={navigationNextRef}
          className={clsx(
            'episode-row-navigation-button button-primary cursor-pointer disabled:cursor-not-allowed disabled:bg-(--color-button-primary-normal-background)',
            transitionClasses,
          )}
          aria-controls={`${episodeRowId}-swiper`}
          aria-label="Next Slide"
          tabIndex={0}
        >
          <span className="sr-only!">Next</span>
          <svg className="button-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
            <path
              fill="currentColor"
              d="M438.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.8 224H32c-17.7 0-32 14.3-32 32s14.3 32 32 32h306.7L233.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160z"
            />
          </svg>
        </button>
      </div>
    </Section>
  )
}

export default EpisodeRowClient
